import type { InkPoint, Pt } from './types.js';
import { dist } from './geometry.js';

/**
 * "Hold to straighten" (decision I4): replaces a stroke by a straight line from its first to its
 * last point; both ends get the average pressure, the duration is kept in the last point's dt.
 */
export function straighten(points: readonly InkPoint[]): InkPoint[] {
  if (points.length < 2) return points.map((p) => ({ ...p }));
  const first = points[0]!;
  const last = points[points.length - 1]!;
  const p = points.reduce((s, q) => s + q.p, 0) / points.length;
  const a: InkPoint = { x: first.x, y: first.y, p };
  const b: InkPoint = { x: last.x, y: last.y, p };
  if (points.some((q) => q.dt !== undefined)) {
    a.dt = first.dt ?? 0;
    b.dt = points.slice(1).reduce((s, q) => s + (q.dt ?? 0), 0);
  }
  return [a, b];
}

/** A timed sample of the pointer (screen px, absolute ms). */
export interface TimedPt extends Pt {
  t: number;
}

export interface HoldOptions {
  /** How long the pointer must rest (ms). */
  ms?: number;
  /** Allowed jitter radius (px). */
  radius?: number;
  /** Minimum stroke length before a hold counts (px) – a dot is not straightened. */
  minLength?: number;
}

export const HOLD_MS = 500;
export const HOLD_RADIUS_PX = 4;
export const HOLD_MIN_LENGTH_PX = 12;

/**
 * True when the pointer has rested within `radius` of its last position for at least `ms`
 * (every sample of the last `ms` milliseconds counts) and the stroke is long enough.
 * `now` is the current time: a pointer that does not move produces no samples.
 */
export function isHolding(
  samples: readonly TimedPt[],
  now: number,
  opts: HoldOptions = {},
): boolean {
  const ms = opts.ms ?? HOLD_MS;
  const radius = opts.radius ?? HOLD_RADIUS_PX;
  const minLength = opts.minLength ?? HOLD_MIN_LENGTH_PX;
  const last = samples[samples.length - 1];
  if (last === undefined || now - samples[0]!.t < ms) return false;
  let length = 0;
  for (let i = 1; i < samples.length; i++) length += dist(samples[i - 1]!, samples[i]!);
  if (length < minLength) return false;
  // Every sample inside the window (now - ms, now] plus the one where the window starts
  // (exists: the first sample is at least `ms` old) must lie within `radius`.
  let i = samples.length - 1;
  while (samples[i]!.t > now - ms) {
    if (dist(samples[i]!, last) > radius) return false;
    i--;
  }
  return dist(samples[i]!, last) <= radius;
}
